import React, { useState, useEffect } from 'react';
import { IonButton } from '@ionic/react';
import { InviteTeamModal } from './InviteTeamModal';
import { createTeam, loadTeamFromProfile } from '../../services/teamService';
import { useProfileStore } from '../../store/useProfileStore';
import { whoAmI } from '../../services/authService';
import { getAccountFormUrl, useAccountForm } from '@/utils/accounts';

const SEATS_ICON_URL = 'https://storage.googleapis.com/rev-public/icons/Seats.svg';

interface SeatsWidgetProps {
  usedSeats: number;
  totalSeats: number;
  onTeamDataRefresh?: () => Promise<void> | void;
}

export const SeatsWidget: React.FC<SeatsWidgetProps> = ({ usedSeats, totalSeats, onTeamDataRefresh }) => {
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const { activeTeam } = useProfileStore();

  // Load team from profile's businessId on mount
  useEffect(() => {
    const loadTeam = async () => {
      const team = await loadTeamFromProfile();
    };
    loadTeam();
  }, []);

  // Calculate remaining seats for button logic
  const remainingSeats = totalSeats - usedSeats;

  // Determine button text based on seat status
  const getButtonText = () => {
    if (!activeTeam) {
      return 'Create A Team';
    } else if (remainingSeats === 0 && totalSeats === 0) {
      return '+ Invite';
    } else if (remainingSeats < totalSeats && remainingSeats > 0) {
      return '+ Invite';
    } else if (remainingSeats === totalSeats && remainingSeats > 0) {
      return 'Add Seats';
    } else if (remainingSeats === 0 && totalSeats > 0) {
      return 'Add Seats';
    } else {
      return '+ Invite';
    }
  };

  const buttonText = getButtonText();

  const handleInviteClick = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!activeTeam && useAccountForm()) {
      const accountFormUrl = getAccountFormUrl();
      if (accountFormUrl) {
        window.open(accountFormUrl, '_blank');
      }
    } else {
      setIsInviteModalOpen(true);
    }
  };

  const handleSaveTeamName = async (name: string) => {
    // Prevent duplicate team creation
    if (activeTeam) {
      return;
    }

    const response = await createTeam(name);
    
    await whoAmI();
  };

  return (
    <div className="bg-white flex w-full sm:min-w-60 flex-col px-5 py-4 rounded-[10px]">
      {/* Row 1: Icon and Title */}
      <div className="flex w-full items-center">
        <div className="flex items-center gap-3">
          <img
            src={SEATS_ICON_URL}
            alt="Seats"
            className="w-8 h-8"
          />
          <div className="text-[#2D2D2D] text-base font-medium">
            Seats
          </div>
        </div>
      </div>

      {/* Row 2: Seats available text and button - bottom aligned */}
      <div className="flex w-full items-end justify-between mt-auto pt-3">
        <div className="text-[#928881] text-base font-normal">
          Seats available: {totalSeats === 0 ? '0/0' : `${totalSeats - usedSeats}/${totalSeats}`}
        </div>
        <IonButton
          color="primary"
          onClick={handleInviteClick}
        >
          {buttonText}
        </IonButton>
      </div>

      {/* Invite Team Modal */}
      <InviteTeamModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        usedSeats={usedSeats}
        totalSeats={totalSeats}
        teamName={activeTeam?.name}
        teamLogoUrl={activeTeam?.logoUrl}
        seatPriceMonthly={activeTeam?.activePlan?.seatPriceMonthly}
        seatPriceAnnual={activeTeam?.activePlan?.seatPriceAnnual}
        onSaveTeamName={handleSaveTeamName}
        onInvitationsSent={onTeamDataRefresh}
      />
    </div>
  );
};
